/**
 * Lightweight global crash diagnostics for first-paint failures.
 *
 * Goals:
 *  - Catch synchronous errors and unhandled promise rejections that occur
 *    BEFORE the React error boundary mounts (e.g. module init crashes).
 *  - Detect "blank screen" state — if #root is still empty 8s after boot,
 *    surface a static fallback so users on Firefox/Edge/Chrome don't stare
 *    at a white or black void.
 *  - Verify the theme class on <html> reflects the resolved theme; if not,
 *    force light on public landing routes (defensive double-check on top of
 *    the inline boot script in index.html).
 */

const ROLLBACK_KEY = "bantoo_bundle_rollback";


const escapeHtml = (value: unknown) => String(value).slice(0, 240).replace(/[<>&]/g, (char) => {
  if (char === "<") return "&lt;";
  if (char === ">") return "&gt;";
  return "&amp;";
});

const fallbackMarkup = (reason: string, detail?: unknown) => `
  <main data-bantoo-startup-fallback="${reason}" style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#F5F7FA;color:#0F172A;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;box-sizing:border-box;">
    <div style="max-width:560px;text-align:center;">
      <h1 style="font-size:28px;font-weight:700;margin:0 0 12px;font-family:'Space Grotesk',sans-serif;color:#0F172A;">BantooMoney</h1>
      <p style="font-size:16px;line-height:1.5;margin:0 0 24px;color:#334155;">
        The app is taking longer than expected to load. Please refresh once to get the latest version.
      </p>
      <button onclick="window.location.reload()" style="background:#F59E0B;color:#0F172A;border:0;padding:12px 24px;border-radius:8px;font-weight:700;cursor:pointer;font-size:15px;">Reload page</button>
      <button onclick="try{localStorage.setItem('${ROLLBACK_KEY}','safe-landing');location.href='/?bantoo_safe=1'}catch(e){location.href='/?bantoo_safe=1'}" style="margin-left:8px;background:#0F172A;color:#F8FAFC;border:0;padding:12px 24px;border-radius:8px;font-weight:700;cursor:pointer;font-size:15px;">Safe mode</button>
      <button onclick="window.__BANTOO_RUN_HEALTH_CHECKS__&&window.__BANTOO_RUN_HEALTH_CHECKS__().then(function(r){alert(JSON.stringify(r,null,2))})" style="margin-left:8px;background:#E2E8F0;color:#0F172A;border:0;padding:12px 18px;border-radius:8px;font-weight:700;cursor:pointer;font-size:15px;">Health</button>
      <p style="margin-top:24px;font-size:12px;opacity:.75;color:#334155;">
        <a href="/login" style="color:#0F172A;margin-right:12px;">Sign in</a>
        <a href="/signup" style="color:#0F172A;">Create account</a>
      </p>
      ${detail ? `<pre style="margin-top:16px;white-space:pre-wrap;text-align:left;font-size:11px;color:#64748B;max-height:84px;overflow:auto;">${escapeHtml(detail)}</pre>` : ""}
    </div>
  </main>
`;

export const reportBootError = (phase: string, error: unknown) => {
  const payload = {
    phase,
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack?.split("\n").slice(0, 6).join("\n") : undefined,
    path: window.location.pathname,
    userAgent: navigator.userAgent,
    time: new Date().toISOString(),
  };
  const target = window as typeof window & { __BANTOO_BOOT_ERRORS__?: typeof payload[] };
  target.__BANTOO_BOOT_ERRORS__ = [...(target.__BANTOO_BOOT_ERRORS__ || []).slice(-9), payload];
  try {
    const previous = JSON.parse(sessionStorage.getItem("bantoo_boot_errors") || "[]");
    sessionStorage.setItem("bantoo_boot_errors", JSON.stringify([...previous.slice(-4), payload]));
  } catch { /* storage may be unavailable */ }
  window.dispatchEvent(new CustomEvent("bantoo:boot-error", { detail: payload }));
  // eslint-disable-next-line no-console
  console.error("[crash-diagnostics] boot-error", payload);
};

export const shouldUseSafeLanding = () => {
  try {
    const params = new URLSearchParams(window.location.search || "");
    if (params.has("bantoo_force_app")) {
      localStorage.removeItem(ROLLBACK_KEY);
      return false;
    }
    return params.has("bantoo_safe") || localStorage.getItem(ROLLBACK_KEY) === "safe-landing";
  } catch {
    return false;
  }
};

export const renderStartupFallback = (reason: string, error?: unknown) => {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML = fallbackMarkup(reason, error instanceof Error ? error.message : error);
};

const reactHasMounted = () => {
  const root = document.getElementById("root");
  if (!root) return false;
  // React signals mount via attribute (set in bootstrap on commit).
  if (root.hasAttribute("data-bantoo-react-mounted")) return true;
  // Heuristic: any non-fallback element child means React (or some other code) committed real DOM.
  const fallback = root.querySelector("[data-bantoo-startup-fallback]");
  if (root.childElementCount === 0) return false;
  if (!fallback) return true;
  // Only the static server fallback is present → React has NOT mounted yet.
  return root.childElementCount > 1;
};

const renderBlankFallback = () => {
  // Critical: never overwrite a successfully-mounted React tree. Earlier
  // versions of this watchdog wiped the live app and replaced it with the
  // static fallback, which is exactly the "blank screen" users reported.
  if (reactHasMounted()) return;
  reportBootError("startup-timeout", new Error(`React did not mount within watchdog window; readyState=${document.readyState}`));
  renderStartupFallback("startup-timeout");
};

const verifyTheme = () => {
  try {
    const html = document.documentElement;
    const stored = localStorage.getItem("bantoo_theme");
    if (stored === "light" && html.classList.contains("dark")) {
      // Defensive: an explicit light choice must never be overridden.
      html.classList.remove("dark");
    }
  } catch {
    /* ignore */
  }
};


let installed = false;

export function installCrashDiagnostics() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  verifyTheme();

  window.addEventListener("error", (event) => {
    reportBootError("window.error", event.error || event.message);
    // eslint-disable-next-line no-console
    console.error("[crash-diagnostics] window.error", {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      col: event.colno,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportBootError("unhandledrejection", event.reason);
    // eslint-disable-next-line no-console
    console.error("[crash-diagnostics] unhandledrejection", {
      reason: String(event.reason),
      path: window.location.pathname,
      userAgent: navigator.userAgent,
    });
  });

  // Blank-screen watchdog. 20s is generous enough for a heavy bundle on slow
  // 3G/4G in Africa to fully load + hydrate without false positives, but short
  // enough that truly stuck users still get a recovery UI. Critically, the
  // watchdog NEVER overwrites a mounted React tree (see reactHasMounted).
  window.setTimeout(renderBlankFallback, 20000);
}
